// ============================================================
//  ADMIN — TEAMS (inkl. Trainer-Liste + Kader-Verwaltung pro Team)
// ============================================================
import { useEffect, useState } from "react";
import { fetchTeams, createTeam, updateTeam, deleteTeam } from "../../cms/teams";
import { fetchSpieler, createSpieler, updateSpieler, deleteSpieler } from "../../cms/spieler";
import { fotoHochladen } from "../../cms/upload";

const LEER = {
  name: "", jahrgang: "", liga: "", beschreibung: "",
  trainer: [], bild: "", akzentfarbe: "", sortierung: 0, spielplanUrl: "",
};

const SPIELER_LEER = { name: "", foto: "", sortierung: 0 };

// "trainer" kann historisch ein String (altes CMS-Formular) oder ein
// Array von {name, rolle} (Sanity-Import) sein — hier fürs Bearbeiten
// immer auf ein Array von {name, rolle} normalisieren.
function normalizeTrainer(t) {
  if (Array.isArray(t)) return t.map((x) => ({ name: x?.name || "", rolle: x?.rolle || "" }));
  if (typeof t === "string" && t.trim()) return [{ name: t.trim(), rolle: "" }];
  return [];
}

export default function Teams() {
  const [liste, setListe] = useState([]);
  const [ladend, setLadend] = useState(true);
  const [formular, setFormular] = useState(null);
  const [hochladen, setHochladen] = useState(false);
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const [fehler, setFehler] = useState("");

  // Kader (Spieler) des gerade bearbeiteten Teams
  const [spielerListe, setSpielerListe] = useState([]);
  const [spielerLadend, setSpielerLadend] = useState(false);
  const [spielerFormular, setSpielerFormular] = useState(null);
  const [spielerHochladen, setSpielerHochladen] = useState(false);
  const [spielerSpeichernLaeuft, setSpielerSpeichernLaeuft] = useState(false);
  const [spielerFehler, setSpielerFehler] = useState("");

  async function neuLaden() {
    setLadend(true);
    setListe(await fetchTeams());
    setLadend(false);
  }
  useEffect(() => { neuLaden(); }, []);

  // Kader neu laden, sobald ein bestehendes Team zum Bearbeiten geöffnet wird
  useEffect(() => {
    setSpielerFormular(null);
    setSpielerFehler("");
    if (formular?.id) {
      spielerNeuLaden(formular.id);
    } else {
      setSpielerListe([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formular?.id]);

  function oeffnen(eintrag) {
    setFormular(
      eintrag
        ? { ...eintrag, trainer: normalizeTrainer(eintrag.trainer) }
        : { ...LEER, trainer: [] }
    );
    setFehler("");
  }

  // ---- Trainer-Liste ----

  function trainerHinzufuegen() {
    setFormular((f) => ({ ...f, trainer: [...f.trainer, { name: "", rolle: "" }] }));
  }

  function trainerAendern(index, feld, wert) {
    setFormular((f) => {
      const trainer = [...f.trainer];
      trainer[index] = { ...trainer[index], [feld]: wert };
      return { ...f, trainer };
    });
  }

  function trainerEntfernen(index) {
    setFormular((f) => ({ ...f, trainer: f.trainer.filter((_, i) => i !== index) }));
  }

  async function bildAuswahl(e) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setHochladen(true);
    setFehler("");
    try {
      const url = await fotoHochladen(datei, "cms");
      setFormular((f) => ({ ...f, bild: url }));
    } catch (err) {
      setFehler(err.message);
    } finally {
      setHochladen(false);
      e.target.value = "";
    }
  }

  async function speichern(e) {
    e.preventDefault();
    setSpeichernLaeuft(true);
    setFehler("");
    try {
      const daten = {
        ...formular,
        sortierung: Number(formular.sortierung) || 0,
        // Leere Trainer-Zeilen (kein Name eingetragen) nicht mitspeichern
        trainer: formular.trainer.filter((t) => t.name.trim() !== ""),
      };
      if (formular.id) {
        const { id, ...rest } = daten;
        await updateTeam(id, rest);
        setFormular(null);
      } else {
        // Neu erstelltes Team bleibt im Bearbeiten-Modus offen,
        // damit direkt im Anschluss der Kader erfasst werden kann.
        const neueId = await createTeam(daten);
        setFormular({ ...daten, id: neueId });
      }
      await neuLaden();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setSpeichernLaeuft(false);
    }
  }

  async function loeschen(id) {
    if (!window.confirm("Team wirklich löschen? Der komplette Kader wird dabei nicht automatisch gelöscht.")) return;
    await deleteTeam(id);
    if (formular?.id === id) setFormular(null);
    await neuLaden();
  }

  // ---- Kader (Spieler) ----

  async function spielerNeuLaden(teamId) {
    setSpielerLadend(true);
    setSpielerListe(await fetchSpieler(teamId));
    setSpielerLadend(false);
  }

  function spielerOeffnen(eintrag) {
    setSpielerFormular(eintrag ? { ...eintrag } : { ...SPIELER_LEER });
    setSpielerFehler("");
  }

  async function spielerFotoAuswahl(e) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setSpielerHochladen(true);
    setSpielerFehler("");
    try {
      const url = await fotoHochladen(datei, "cms");
      setSpielerFormular((f) => ({ ...f, foto: url }));
    } catch (err) {
      setSpielerFehler(err.message);
    } finally {
      setSpielerHochladen(false);
      e.target.value = "";
    }
  }

  async function spielerSpeichern(e) {
    e.preventDefault();
    setSpielerSpeichernLaeuft(true);
    setSpielerFehler("");
    try {
      const daten = { ...spielerFormular, sortierung: Number(spielerFormular.sortierung) || 0 };
      if (spielerFormular.id) {
        const { id, ...rest } = daten;
        await updateSpieler(formular.id, id, rest);
      } else {
        await createSpieler(formular.id, daten);
      }
      setSpielerFormular(null);
      await spielerNeuLaden(formular.id);
    } catch (err) {
      setSpielerFehler(err.message);
    } finally {
      setSpielerSpeichernLaeuft(false);
    }
  }

  async function spielerLoeschen(spielerId) {
    if (!window.confirm("Spieler wirklich löschen?")) return;
    await deleteSpieler(formular.id, spielerId);
    await spielerNeuLaden(formular.id);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-extrabold uppercase text-primary">Teams</h1>
        {!formular && (
          <button onClick={() => oeffnen(null)} className="bg-primary text-white font-bold uppercase text-sm px-5 py-2.5 hover:bg-accent transition-colors">
            + Neu
          </button>
        )}
      </div>

      {formular && (
        <form onSubmit={speichern} className="border border-dark/10 p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold">Name *</span>
              <input required value={formular.name} onChange={(e) => setFormular({ ...formular, name: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Jahrgang</span>
              <input value={formular.jahrgang} onChange={(e) => setFormular({ ...formular, jahrgang: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Liga</span>
              <input value={formular.liga} onChange={(e) => setFormular({ ...formular, liga: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Akzentfarbe (Hex)</span>
              <input placeholder="#003568" value={formular.akzentfarbe} onChange={(e) => setFormular({ ...formular, akzentfarbe: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Sortierung</span>
              <input type="number" value={formular.sortierung} onChange={(e) => setFormular({ ...formular, sortierung: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Spielplan-Link</span>
              <input value={formular.spielplanUrl} onChange={(e) => setFormular({ ...formular, spielplanUrl: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
          </div>

          {/* Trainer — mehrere möglich, mit optionaler Rolle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Trainer</span>
              <button type="button" onClick={trainerHinzufuegen} className="text-xs font-semibold text-primary hover:text-accent">
                + Trainer
              </button>
            </div>
            {formular.trainer.length === 0 ? (
              <p className="text-sm text-dark/40">Noch kein Trainer erfasst.</p>
            ) : (
              <div className="space-y-2">
                {formular.trainer.map((t, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      placeholder="Name"
                      value={t.name}
                      onChange={(e) => trainerAendern(i, "name", e.target.value)}
                      className="flex-1 border border-dark/20 px-3 py-2"
                    />
                    <input
                      placeholder="Rolle (optional)"
                      value={t.rolle}
                      onChange={(e) => trainerAendern(i, "rolle", e.target.value)}
                      className="flex-1 border border-dark/20 px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={() => trainerEntfernen(i)}
                      aria-label="Trainer entfernen"
                      className="text-red-600 hover:text-red-800 px-2"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="block">
            <span className="text-sm font-semibold">Beschreibung</span>
            <textarea rows={4} value={formular.beschreibung} onChange={(e) => setFormular({ ...formular, beschreibung: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
          </label>

          <div>
            <span className="text-sm font-semibold">Bild</span>
            {formular.bild && <img src={formular.bild} alt="" className="mt-2 h-24 w-24 object-cover" />}
            <input type="file" accept="image/*" onChange={bildAuswahl} disabled={hochladen} className="mt-2 text-sm block" />
            {hochladen && <p className="text-sm text-dark/50 mt-1">Lädt hoch …</p>}
          </div>

          {fehler && <p className="text-sm text-red-600">{fehler}</p>}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={speichernLaeuft} className="bg-primary text-white font-bold uppercase text-sm px-6 py-2.5 hover:bg-accent transition-colors disabled:opacity-50">
              {speichernLaeuft ? "Speichert …" : "Speichern"}
            </button>
            <button type="button" onClick={() => setFormular(null)} className="font-semibold text-sm text-dark/60 hover:text-primary">
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {/* Kader — erst sichtbar, wenn das Team schon gespeichert ist */}
      {formular?.id && (
        <div className="border border-dark/10 p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-extrabold uppercase text-primary">Kader — {formular.name}</h2>
            {!spielerFormular && (
              <button onClick={() => spielerOeffnen(null)} className="bg-primary text-white font-bold uppercase text-xs px-4 py-2 hover:bg-accent transition-colors">
                + Spieler
              </button>
            )}
          </div>

          {spielerFormular && (
            <form onSubmit={spielerSpeichern} className="border border-dark/10 p-4 mb-6 space-y-3 bg-dark/[0.02]">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-semibold">Name *</span>
                  <input required value={spielerFormular.name} onChange={(e) => setSpielerFormular({ ...spielerFormular, name: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold">Sortierung</span>
                  <input type="number" value={spielerFormular.sortierung} onChange={(e) => setSpielerFormular({ ...spielerFormular, sortierung: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
                </label>
              </div>

              <div>
                <span className="text-sm font-semibold">Foto</span>
                {spielerFormular.foto && <img src={spielerFormular.foto} alt="" className="mt-2 h-20 w-20 object-cover" />}
                <input type="file" accept="image/*" onChange={spielerFotoAuswahl} disabled={spielerHochladen} className="mt-2 text-sm block" />
                {spielerHochladen && <p className="text-sm text-dark/50 mt-1">Lädt hoch …</p>}
              </div>

              {spielerFehler && <p className="text-sm text-red-600">{spielerFehler}</p>}

              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={spielerSpeichernLaeuft} className="bg-primary text-white font-bold uppercase text-xs px-5 py-2 hover:bg-accent transition-colors disabled:opacity-50">
                  {spielerSpeichernLaeuft ? "Speichert …" : "Speichern"}
                </button>
                <button type="button" onClick={() => setSpielerFormular(null)} className="font-semibold text-xs text-dark/60 hover:text-primary">
                  Abbrechen
                </button>
              </div>
            </form>
          )}

          {spielerLadend ? (
            <p className="text-dark/50 text-sm">Lädt …</p>
          ) : spielerListe.length === 0 ? (
            <p className="text-dark/50 text-sm">Noch keine Spieler erfasst.</p>
          ) : (
            <ul className="divide-y divide-dark/10">
              {spielerListe.map((s) => (
                <li key={s.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {s.foto && <img src={s.foto} alt="" className="h-10 w-10 object-cover flex-shrink-0" />}
                    <p className="font-semibold text-sm truncate">{s.name}</p>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <button onClick={() => spielerOeffnen(s)} className="text-xs font-semibold text-primary hover:text-accent">Bearbeiten</button>
                    <button onClick={() => spielerLoeschen(s.id)} className="text-xs font-semibold text-red-600 hover:text-red-800">Löschen</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {ladend ? (
        <p className="text-dark/50">Lädt …</p>
      ) : (
        <ul className="divide-y divide-dark/10">
          {liste.map((t) => (
            <li key={t.id} className="py-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-bold">{t.name}</p>
                <p className="text-sm text-dark/50">{t.jahrgang} {t.liga && `— ${t.liga}`}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <button onClick={() => oeffnen(t)} className="text-sm font-semibold text-primary hover:text-accent">Bearbeiten</button>
                <button onClick={() => loeschen(t.id)} className="text-sm font-semibold text-red-600 hover:text-red-800">Löschen</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
